import { ReactComponent as AlertCircle } from "assets/images/iconsV2/alert-circle.svg";
import { ReactComponent as Cross } from "assets/images/iconsV2/cross.svg";
import { ReactComponent as FileIcon } from "assets/images/iconsV2/file.svg";
import { ReactComponent as FolderZip } from "assets/images/iconsV2/folder-zip.svg";
import { ReactComponent as Refresh } from "assets/images/iconsV2/refresh.svg";
import LoadingSpinner from "components/v2/LoadingSpinner/LoadingSpinner";
import { classNames } from "helpers/commonHelpers";
import "./FilePreview.scss";

const ZIP_FILENAME = /\.zip$/i;

interface FilePreviewProps {
  /**
   * The name of the file.
   */
  fileName?: string;
  /**
   * The size of the file.
   */
  fileSize?: string;
  /**
   * The URL of the file.
   */
  url?: string;
  /**
   * Loading state of the file preview.
   */
  isLoading?: boolean;
  /**
   * Error state of the file preview.
   */
  isError?: boolean;
  /**
   * Error message to display when isError is true.
   */
  errorMessage?: string;
  /**
   * Event handler for close button
   */
  onClose?: () => void;
  /**
   * Event handler for refresh button
   */
  onRefresh?: () => void;
  /**
   * Event handler for click event which will block the url from opening in a new tab
   */
  onClick?: () => void;
}

const fileTypeIcon = (fileName?: string) => {
  if (fileName && ZIP_FILENAME.test(fileName)) {
    return <FolderZip />;
  }
  return <FileIcon />;
};

export default function FilePreview({
  fileName,
  fileSize,
  url,
  isLoading,
  isError,
  errorMessage,
  onClose,
  onRefresh,
  onClick,
}: FilePreviewProps) {
  const showActions = isLoading || !!onClose || (isError && !!onRefresh);

  const mainContent = (
    <div className="file-preview__text">
      <p className="file-preview__file-name" title={fileName}>
        {fileName}
      </p>
      {fileSize && !errorMessage && <p className="file-preview__file-size">{fileSize}</p>}
      {errorMessage && <p className="file-preview__error-message">{errorMessage}</p>}
    </div>
  );
  return (
    <div className={classNames("file-preview", { "file-preview__error": isError })}>
      <div className="file-preview__content">
        {isError ? <AlertCircle /> : fileTypeIcon(fileName)}
        {onClick && !isLoading ? (
          <div onClick={onClick} className="file-preview__downloadable">
            {mainContent}
          </div>
        ) : url ? (
          <a href={url} target="_blank" rel="noreferrer" className="file-preview__link">
            {mainContent}
          </a>
        ) : (
          mainContent
        )}
      </div>
      {showActions && (
        <div className="file-preview__actions">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="d-flex align-items-center gap-lg">
              {isError && onRefresh && (
                <div className="cursor-pointer" onClick={onRefresh}>
                  <Refresh />
                </div>
              )}
              {onClose && (
                <div className="cursor-pointer" onClick={onClose}>
                  <Cross width={16} height={16} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

FilePreview.defaultProps = {
  fileName: undefined,
  fileSize: undefined,
  url: undefined,
  isLoading: false,
  isError: false,
  errorMessage: undefined,
  onClose: undefined,
  onRefresh: undefined,
  onClick: undefined,
};
